import React, { useEffect, useState, useMemo, useCallback } from "react";
import { ListLayout } from "components/common/ListLayout";
import TableHeaderItem from "components/common/ListLayout/TableHeaderItem";
import { Chip } from "@mui/material";
import Api from "services/api";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import EditDepartmentNotificationModal from "components/settings/EditDepartmentNotificationModal";

function DepartmentNotificationList() {
  const { t } = useTranslation();
  const [departments, setDepartments] = useState([]);
  const [loader, setLoader] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editModal, setEditModal] = useState({
    isOpen: false,
    item: null,
  });

  const dummyFetchList = useCallback(() => () => {}, []);

  const fetchDepartmentNotifications = useCallback(async () => {
    setLoader(true);
    try {
      const response = await Api.getDepartmentNotifications();
      if (response?.status === 200 || response?.data?.success) {
        setDepartments(response?.data?.data || []);
      } else {
        toast.error(
          response?.data?.message || t("common.failedToFetch", "Failed to load data."),
          { className: "errorToast" }
        );
      }
    } catch (err) {
      toast.error(err?.message || t("common.anErrorOccurred", "An error occurred."), {
        className: "errorToast",
      });
    } finally {
      setLoader(false);
    }
  }, [t]);

  useEffect(() => {
    fetchDepartmentNotifications();
  }, [fetchDepartmentNotifications]);

  const handleOpenEdit = (item) => {
    setEditModal({
      isOpen: true,
      item,
    });
  };

  const handleCloseEdit = () => {
    setEditModal({
      isOpen: false,
      item: null,
    });
  };

  const filteredDepartments = useMemo(() => {
    if (!searchTerm.trim()) return departments;
    const term = searchTerm.toLowerCase();
    return departments.filter(
      (dept) =>
        dept.department_name?.toLowerCase().includes(term) ||
        dept.email?.toLowerCase().includes(term)
    );
  }, [departments, searchTerm]);

  const totalPages = useMemo(() => {
    return Math.ceil(filteredDepartments.length / 10) || 1;
  }, [filteredDepartments.length]);

  const renderTableHeader = useCallback(() => {
    return (
      <tr>
        <th>#</th>
        <th>
          {t("settings.departmentName", "Department")}
        </th>
        <th>
          {t("settings.recipientEmails", "Notification Recipients (Email)")}
        </th>
        <th>
          {t("table.actions", "Actions")}
        </th>
      </tr>
    );
  }, [t]);

  const renderTableContent = useCallback(() => {
    if (!filteredDepartments.length) {
      return (
        <tr>
          <td colSpan="4" className="text-center py-4">
            {t("common.noRecordsFound", "No records found")}
          </td>
        </tr>
      );
    }

    return filteredDepartments.map((dept, index) => {
      const emailList = dept.email
        ? dept.email
            .split(",")
            .map((e) => e.trim())
            .filter(Boolean)
        : [];

      return (
        <tr key={dept.id}>
          <td>
            <span>{index + 1}</span>
          </td>
          <td>
            <strong>
              {dept.department_name}
            </strong>
          </td>
          <td>
            {emailList.length > 0 ? (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px",
                  alignItems: "center",
                }}
              >
                {emailList.map((em, idx) => (
                  <Chip
                    key={idx}
                    label={em}
                    size="small"
                    style={{
                      backgroundColor: "#e8f0fe",
                      color: "#1967d2",
                      fontWeight: 500,
                      borderRadius: "6px",
                    }}
                  />
                ))}
              </div>
            ) : (
              <span>
                {t("settings.noRecipientsConfigured", "Not Configured (Default)")}
              </span>
            )}
          </td>
          <td>
            <div>
              <button
                className="table_edit"
                onClick={() => handleOpenEdit(dept)}
                title={t("vendors.edit", "Edit")}
              >
                {t("vendors.edit", "Edit")}
              </button>
            </div>
          </td>
        </tr>
      );
    });
  }, [filteredDepartments, t]);

  return (
    <>
      <ListLayout
        heading={t("sidebar.notificationRecipients", "Notification Recipients")}
        time={true}
        title=""
        buttonTitle=""
        disableBootstrap={true}
        renderTableHeader={renderTableHeader}
        renderTableContent={renderTableContent}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        loader={loader}
        searchPlaceholder={t(
          "settings.searchDepartmentPlaceholder",
          "Search department or email..."
        )}
        noOfItems={filteredDepartments.length}
        defaultPageLength={10}
        addButtonDisabled={true}
        totalPages={totalPages}
        fetchList={dummyFetchList}
      />

      <EditDepartmentNotificationModal
        item={editModal.item}
        isOpen={editModal.isOpen}
        onClose={handleCloseEdit}
        onSuccess={fetchDepartmentNotifications}
      />
    </>
  );
}

export default DepartmentNotificationList;
